import { api } from './client';

export const authApi = {
  // === 사용자 관리 (admin) ===
  getUsers: async (): Promise<any[]> => {
    const response = await api.get('/api/auth/users');
    return response.data;
  },

  createUser: async (data: { username: string; password: string; role: string; display_name?: string; employee_id?: string }): Promise<any> => {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },

  register: async (data: { username: string; password: string; role?: string; company_id?: number }): Promise<any> => {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },

  updateUserRole: async (userId: number, role: string): Promise<any> => {
    const response = await api.put(`/api/auth/users/${userId}`, { role }, {
      validateStatus: (s) => s === 200 || s === 202,
    });
    return { ...response.data, _httpStatus: response.status };
  },

  deleteUser: async (userId: number): Promise<any> => {
    const response = await api.delete(`/api/auth/users/${userId}`);
    return response.data;
  },

  // === 권한 변경 이중 승인 ===
  getRoleRequests: async (): Promise<any> => {
    const response = await api.get('/api/role-requests');
    return response.data;
  },

  getRoleApprovers: async (): Promise<any> => {
    const response = await api.get('/api/role-requests/approvers');
    return response.data;
  },

  approveRoleRequest: async (requestId: number): Promise<any> => {
    const response = await api.post(`/api/role-requests/${requestId}/approve`);
    return response.data;
  },

  rejectRoleRequest: async (requestId: number, reason?: string): Promise<any> => {
    const response = await api.post(`/api/role-requests/${requestId}/reject`, { reason });
    return response.data;
  },

  cancelRoleRequest: async (requestId: number): Promise<any> => {
    const response = await api.post(`/api/role-requests/${requestId}/cancel`);
    return response.data;
  },

  // === LV2 step-up 상태 확인 ===
  checkLv2Status: async (): Promise<{ lv2_active: boolean; su_expires_in: number | null }> => {
    const response = await api.get('/api/auth/lv2-status');
    return response.data;
  },

  // === PII 마스크 해제 ===
  verifyPiiPassword: async (password: string): Promise<{ success: boolean; bypass: boolean }> => {
    const response = await api.post('/api/auth/verify-pii-password', { password });
    return response.data;
  },

  checkPiiBypass: async (): Promise<{ bypass: boolean }> => {
    // MYB-66: Cache result in sessionStorage (5-min TTL) to avoid hitting the
    // /api/auth/* rate limit when browsing through multiple employee records.
    // sessionStorage is scoped to one tab/session, so one user per storage.
    const CACHE_KEY = 'tscorp_pii_bypass';
    const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { bypass, expiresAt } = JSON.parse(cached) as { bypass: boolean; expiresAt: number };
        if (Date.now() < expiresAt) {
          return { bypass };
        }
        sessionStorage.removeItem(CACHE_KEY);
      }
    } catch {
      // sessionStorage unavailable or parse error — fall through to API call
    }

    const response = await api.get('/api/auth/pii-bypass-check');
    const result: { bypass: boolean } = response.data;

    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        bypass: result.bypass,
        expiresAt: Date.now() + CACHE_TTL_MS,
      }));
    } catch {
      // sessionStorage quota exceeded or unavailable — ignore, result still returned
    }

    return result;
  },

  // === View-As ===
  getViewAsOptions: async (): Promise<any> => {
    const response = await api.get('/api/auth/view-as-options');
    return response.data;
  },

  // === 온보딩 튜토리얼 ===
  getOnboardingSteps: async (role?: string): Promise<any> => {
    const params: any = {};
    if (role) params.role = role;
    const response = await api.get('/api/onboarding/steps', { params });
    return response.data;
  },

  getOnboardingStepDetail: async (stepId: string, role?: string): Promise<any> => {
    const params: any = {};
    if (role) params.role = role;
    const response = await api.get(`/api/onboarding/steps/${stepId}`, { params });
    return response.data;
  },

  getOnboardingStatus: async (): Promise<{ user_id: string; completed_steps: string[]; is_completed: number; skipped: number }> => {
    const response = await api.get('/api/onboarding/status');
    return response.data;
  },

  completeOnboardingStep: async (stepId: string, role?: string): Promise<any> => {
    const response = await api.post('/api/onboarding/complete-step', { step_id: stepId, role });
    return response.data;
  },

  skipOnboarding: async (): Promise<any> => {
    const response = await api.post('/api/onboarding/skip');
    return response.data;
  },

  resetOnboarding: async (): Promise<any> => {
    const response = await api.post('/api/onboarding/reset');
    return response.data;
  },

  // === 즐겨찾기 ===
  getFavorites: async (): Promise<any> => {
    const response = await api.get('/api/favorites/');
    return response.data;
  },

  addFavorite: async (targetType: string, targetId: string, label?: string, icon?: string): Promise<any> => {
    const response = await api.post('/api/favorites/', { target_type: targetType, target_id: targetId, label, icon });
    return response.data;
  },

  removeFavorite: async (targetType: string, targetId: string): Promise<any> => {
    const response = await api.delete('/api/favorites/', { params: { target_type: targetType, target_id: targetId } });
    return response.data;
  },

  removeFavoriteById: async (favId: number): Promise<any> => {
    const response = await api.delete(`/api/favorites/${favId}`);
    return response.data;
  },

  checkFavorite: async (targetType: string, targetId: string): Promise<any> => {
    const response = await api.get('/api/favorites/check', { params: { target_type: targetType, target_id: targetId } });
    return response.data;
  },
};
